import React, { useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Alert, IconButton,
  InputAdornment, Divider, Chip,
} from '@mui/material';
import { Visibility, VisibilityOff, East, ArrowBack } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useThemeMode, ForceLightTheme, MONO } from '../../context/ThemeContext';
import { useAuth, DEMO_CREDENTIALS, HOME_FOR_ROLE } from '../../context/AuthContext';

// Light-only like the rest of the app (owner decision 2026-08-10).
function LoginInner() {
  const { palette, isDark } = useThemeMode();
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const params = new URLSearchParams(location.search);
  const redirect = params.get('redirect');

  // If already logged in, redirect via <Navigate> (idiomatic react-router;
  // calling navigate() during render and returning null causes a blank flash)
  if (isAuthenticated) {
    const dest = redirect ? decodeURIComponent(redirect) : HOME_FOR_ROLE[user.role] || '/';
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await login(email, password);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    const dest = redirect ? decodeURIComponent(redirect) : HOME_FOR_ROLE[result.user.role] || '/';
    navigate(dest, { replace: true });
  };

  const fillDemo = (demoEmail, demoPassword) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
    setError('');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: palette.bg, transition: 'background-color 0.3s' }}>
      {/* Left brand panel (desktop only) */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flex: 1,
        bgcolor: palette.heroBg,
        position: 'relative',
        overflow: 'hidden',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: 6,
      }}>
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${isDark ? 'rgba(16,185,129,0.03)' : 'rgba(22,163,74,0.04)'} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? 'rgba(16,185,129,0.03)' : 'rgba(22,163,74,0.04)'} 1px, transparent 1px)`, backgroundSize: '80px 80px' }} />
        <Box sx={{ position: 'absolute', top: '20%', right: '10%', width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${palette.accent}22 0%, transparent 70%)`, filter: 'blur(80px)' }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box component={RouterLink} to="/" sx={{ display: 'inline-flex', textDecoration: 'none' }}>
            <Box component="img" src="/farmgate-logo-reversed.svg" alt="Farmgate" sx={{ height: 44, width: 'auto' }} />
          </Box>
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 480 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: palette.accentLight, letterSpacing: '0.15em', textTransform: 'uppercase', mb: 2 }}>
            Commodity Intelligence
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: { md: '2.4rem', lg: '3rem' }, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.1, mb: 2 }}>
            Welcome back to Farmgate.
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
            Daily commodity prices across Nigeria, global futures, and FX-adjusted analytics - all in one place.
          </Typography>
        </Box>

        <Typography sx={{ position: 'relative', zIndex: 1, fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)' }}>
          © 2026 Farmgate · A product of Triple Gee Merchants Ltd
        </Typography>
      </Box>

      {/* Right form panel */}
      <Box sx={{ flex: { xs: 1, md: 0.9 }, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, md: 6 }, position: 'relative' }}>
        {/* Top bar: back + theme toggle */}
        <Box sx={{ position: 'absolute', top: 16, left: 16, right: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button component={RouterLink} to="/" startIcon={<ArrowBack sx={{ fontSize: '18px !important' }} />}
            sx={{ color: palette.textSecondary, fontSize: '0.8rem', textTransform: 'none', fontWeight: 500, '&:hover': { bgcolor: palette.surfaceHover } }}>
            Back to public site
          </Button>
        </Box>

        <Container maxWidth="xs" sx={{ width: '100%' }}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {/* Mobile logo */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
              <Box component="img" src={isDark ? '/farmgate-logo-reversed.svg' : '/farmgate-logo-primary.svg'} alt="Farmgate" sx={{ height: 40, width: 'auto' }} />
            </Box>

            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: palette.text, letterSpacing: '-0.02em', mb: 0.5 }}>
              Sign in to your account
            </Typography>
            <Typography sx={{ color: palette.textSecondary, fontSize: '0.9rem', mb: 3 }}>
              Access your analytics workspace or admin tools.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2, fontSize: '0.85rem' }} onClose={() => setError('')}>{error}</Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Email" type="email" required fullWidth size="small" autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                InputLabelProps={{ sx: { color: palette.textMuted } }}
                InputProps={{ sx: { color: palette.text, bgcolor: palette.card, '& fieldset': { borderColor: palette.cardBorder }, '&:hover fieldset': { borderColor: palette.accent }, '&.Mui-focused fieldset': { borderColor: palette.accent } } }}
              />
              <TextField
                label="Password" type={showPw ? 'text' : 'password'} required fullWidth size="small"
                value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                InputLabelProps={{ sx: { color: palette.textMuted } }}
                InputProps={{
                  sx: { color: palette.text, bgcolor: palette.card, '& fieldset': { borderColor: palette.cardBorder }, '&:hover fieldset': { borderColor: palette.accent }, '&.Mui-focused fieldset': { borderColor: palette.accent } },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPw((s) => !s)} edge="end" size="small" sx={{ color: palette.textMuted }}>
                        {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ mt: -1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography
                  component={RouterLink}
                  to="/signup"
                  sx={{ fontSize: '0.8rem', color: palette.accent, fontWeight: 600, textDecoration: 'none' }}
                >
                  Create a free account
                </Typography>
              </Box>
              <Box sx={{ mt: -1.5, textAlign: 'right' }}>
                <Typography
                  component={RouterLink}
                  to="/forgot-password"
                  sx={{ fontSize: '0.8rem', color: palette.textSecondary, textDecoration: 'none', '&:hover': { color: palette.accent } }}
                >
                  Forgot your password?
                </Typography>
              </Box>
              <Button type="submit" variant="contained" endIcon={<East sx={{ fontSize: '16px !important' }} />} disabled={submitting}
                sx={{ bgcolor: palette.accent, color: '#fff', fontWeight: 600, py: 1.2, borderRadius: '10px', textTransform: 'none', boxShadow: '0 1px 2px rgba(16,24,40,0.16)', '&:hover': { bgcolor: '#1C9550' }, '&.Mui-disabled': { bgcolor: palette.cardBorder, color: palette.textMuted } }}>
                {submitting ? 'Signing in…' : 'Sign In'}
              </Button>
            </Box>
            {/* Demo credentials are development only. Rendering them in
                production published the admin password to every visitor, on
                the page whose whole job is to keep people out. */}
            {process.env.NODE_ENV === 'development' && (
              <>
                <Divider sx={{ my: 3, borderColor: palette.divider, '&::before, &::after': { borderColor: palette.divider } }}>
                  <Typography sx={{ fontSize: '0.7rem', color: palette.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Demo credentials (dev only)
                  </Typography>
                </Divider>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  {DEMO_CREDENTIALS.map((d) => (
                    <Box key={d.email}
                      onClick={() => fillDemo(d.email, d.password)}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.2, border: `1px solid ${palette.cardBorder}`, borderRadius: '10px', cursor: 'pointer', bgcolor: palette.card, transition: 'all 0.2s', '&:hover': { borderColor: palette.accent, bgcolor: palette.accentBg } }}
                    >
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                          <Chip label={d.role.toUpperCase()} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', bgcolor: d.role === 'admin' ? palette.warningBg : palette.accentBg, color: d.role === 'admin' ? palette.warning : palette.accent }} />
                          <Typography sx={{ fontSize: '0.78rem', color: palette.text, fontWeight: 600 }}>{d.email}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.7rem', color: palette.textMuted, fontFamily: MONO }}>
                          password: {d.password}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.68rem', color: palette.accent, fontWeight: 600 }}>Use →</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}

function Login() {
  return (
    <ForceLightTheme>
      <LoginInner />
    </ForceLightTheme>
  );
}

export default Login;
